import { Injectable } from '@angular/core';
import { TitleCasePipe, UpperCasePipe } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import {
    AuthService,
    CALCULATOR_FIELD_TYPES,
    CALCULATOR_FIELD_UNITS,
    ICalculatorSection, OrganizationService,
    PropertyService
} from '../services';
import { IVestaMessage } from '../interfaces';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as Parse from 'parse';
import { Router } from '@angular/router';

export interface IAddressFrom {
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    type: string;
    status: string;
    offerTerms: string;
}

export interface IEstatedForm {
    structure: any,
    parcel: any,
    deeds: any[],
    assessments: any[],
    taxes: any[]
}

export interface IMarketingForm {
    marketingTitle: string;
    marketingDescription: string;
    access: string;
    marketingPurchasePrice: number;
    acceptedOfferPrice: number;
    deposit: number;
    inspectionExpiration: Date;
    closeDate: Date;
    noInspectionDate: boolean;
    hideZillowInfo: boolean;
}

export interface IAutoSaveStatus {
    inProgress: boolean;
    complete: boolean;
}

@Injectable()
export class PropertyStateService {
    public loading: boolean = false;
    public savingNew: boolean = false;
    public deleting: boolean = false;
    public fetchingEstatedData: boolean = false;
    public reload: boolean = false;
    public isNew: boolean = false;
    public addressUnlocked: boolean = false;
    public property: Parse.Object = new Parse.Object('Property');
    public estatedInfo: Parse.Object = new Parse.Object('EstatedInfo');
    public offerFlip: Parse.Object = new Parse.Object('Offer_Flip');
    public offerRental: Parse.Object = new Parse.Object('Offer_Rental');
    public aaFlip: Parse.Object = new Parse.Object('AA_Flip');
    public aaRental: Parse.Object = new Parse.Object('AA_Rental');
    public saFlip: Parse.Object = new Parse.Object('SA_Flip');
    public saRental: Parse.Object = new Parse.Object('SA_Rental');
    public rehabFlip: Parse.Object = new Parse.Object('Rehab_Flip');
    public rehabRental: Parse.Object = new Parse.Object('Rehab_Rental');
    public vendors: Parse.Object[] = [];

    // contract
    public contract: Parse.Object = new Parse.Object('Contract');
    public reloadContracts: boolean = false;

    public addressForm: IAddressFrom = {
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        type: '',
        status: '',
        offerTerms: ''
    };

    public estatedForm: IEstatedForm = {
        structure: {},
        parcel: {},
        deeds: [{}],
        assessments: [{}],
        taxes: [{}]
    };

    public marketingForm: IMarketingForm = {
        marketingTitle: '',
        marketingDescription: '',
        access: '',
        marketingPurchasePrice: 0,
        acceptedOfferPrice: 0,
        deposit: 0,
        inspectionExpiration: new Date(),
        closeDate: new Date(),
        noInspectionDate: false,
        hideZillowInfo: false
    };

    // Offer Calculator Forms
    public offerCalculator_rentalForm = new FormGroup({}, { updateOn: 'blur' });
    public offerCalculator_flipForm = new FormGroup({}, { updateOn: 'blur' });
    public offerCalculator_rentalOfferForm = new FormGroup({}, { updateOn: 'blur' });
    public offerCalculator_flipOfferForm = new FormGroup({}, { updateOn: 'blur' });

    // AA Flip Forms
    public aaFlip_sharedTopForm = new FormGroup({}, { updateOn: 'blur' });
    public aaFlip_cashForm = new FormGroup({}, { updateOn: 'blur' });
    public aaFlip_financeForm = new FormGroup({}, { updateOn: 'blur' });

    // AA Rental Forms
    public aaRental_sharedTopForm = new FormGroup({}, { updateOn: 'blur' });
    public aaRental_cashForm = new FormGroup({}, { updateOn: 'blur' });
    public aaRental_financeForm = new FormGroup({}, { updateOn: 'blur' });

    // Flip Analysis Forms
    public flipAnalysis_sharedTopForm = new FormGroup({}, { updateOn: 'blur' });
    public flipAnalysis_cashForm = new FormGroup({}, { updateOn: 'blur' });
    public flipAnalysis_financeForm = new FormGroup({}, { updateOn: 'blur' });

    // Rental Analysis Forms
    public rentalAnalysis_sharedTopForm = new FormGroup({}, { updateOn: 'blur' });
    public rentalAnalysis_cashForm = new FormGroup({}, { updateOn: 'blur' });
    public rentalAnalysis_financeForm = new FormGroup({}, { updateOn: 'blur' });
    public rentalAnalysis_refinanceForm = new FormGroup({}, { updateOn: 'blur' });

    // Offer Calculator Sections
    public offerCalculator_rentalCalculationBox: ICalculatorSection[] = [
        {
            tabIndexStart: 1,
            fields: [
                {
                    id: 'list-price',
                    label: 'List Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'commission',
                    label: 'Commission for Selling Agent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        }
    ];

    public offerCalculator_flipCalculationBox: ICalculatorSection[] = [
        {
            tabIndexStart: 100,
            fields: [
                this.offerCalculator_rentalCalculationBox[0].fields[0], // List Price - Use the same model as rentalCalcBox
                {
                    id: 'commission',
                    label: 'Commission for Selling Agent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        }
    ];

    public offerCalculator_rentalOfferCalculatorBox: ICalculatorSection[] = [
        {
            tabIndexStart: 4,
            fields: [
                {
                    id: 'rental-rate',
                    label: 'Rental Rate per Month',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'gross-income',
                    label: 'Annual Gross Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 7,
            fields: [
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'closing-cost',
                    label: 'Estimated Closing Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                },
                {
                    id: 'acqui-cost',
                    label: 'Buyer Acquisition Costs',
                    subLabel: '(Rehab + Simo CC)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'hoa',
                    label: 'Monthly HOA',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                },
                {
                    id: 'property-taxes',
                    label: 'Annual Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                },
                {
                    id: 'insurance',
                    label: 'Annual Insurance',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                },
                {
                    id: 'man-fee',
                    label: 'Management Fee',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 15,
            fields: [
                {
                    id: 'gross-roi',
                    label: 'Desired Investor Gross ROI',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'markup',
                    label: 'Wholesale Markup',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'front-comission',
                    label: 'Front Side Commission Earned',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'sb-spread',
                    label: 'Total SB Spread',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'second-sale-price',
                    label: '2nd Sale Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 21,
            fields: [
                {
                    id: 'net-income',
                    label: 'Annual Net Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-flow',
                    label: 'Monthly Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'net-roi',
                    label: 'Net ROI',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            tabIndexStart: 24,
            fields: [
                {
                    id: 'offer-price',
                    label: 'Offer Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUT_FOOTER,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    public offerCalculator_flipOfferCalculatorBox: ICalculatorSection[] = [
        {
            tabIndexStart: 104,
            fields: [
                {
                    id: 'hold-time',
                    label: 'Hold Time',
                    subLabel: '(months)',
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: null,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'simo-cc',
                    label: 'Estimated Simo CC',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 110,
            fields: [
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-closing',
                    label: 'less Closing Costs',
                    subLabel: '(Est. 2%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'less-commission',
                    label: 'less Commissions',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null
                },
                {
                    id: 'dispo-cost',
                    label: 'Disposition Costs',
                    subLabel: '(Est. 2%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'property-taxes',
                    label: 'Annual Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'prop-tax-holding',
                    label: 'Property Taxes While held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'util-insurance-holding',
                    label: 'Utlities & Insurance while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'misc',
                    label: 'Miscellaneous',
                    subLabel: '(8% of rehab)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 118,
            fields: [
                {
                    id: 'holding-cost',
                    label: 'Holding Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'profit-margin',
                    label: 'Profit Margin on Deal',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'desired-roi',
                    label: 'Desired Investor ROI',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'markup',
                    label: 'Wholesale Markup',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'front-commission',
                    label: 'Front Side Commission',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                },
                {
                    id: 'sb-spread',
                    label: 'Total SB Spread',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'second-sale-price',
                    label: '2nd Sale Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    outputValue: null
                }
            ]
        },
        {
            tabIndexStart: 125,
            fields: [
                {
                    id: 'offer-price',
                    label: 'Offer Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUT_FOOTER,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required]),
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    // AA Flip Sections
    public aaFlip_sharedTopBox: ICalculatorSection[] = [
        {
            tabIndexStart: 1,
            fields: [
                {
                    id: 'purchase-price',
                    label: 'Purchase Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'commission',
                    label: 'Commission for Selling Agent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'hold-time',
                    label: 'Hold Time (months)',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: null,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        }
    ];

    public aaFlip_cashBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'simo-cc',
                    label: 'Estimated Simo CC',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'acqui-cost',
                    label: 'Total Acquisition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [
                {
                    id: 'second-closing',
                    label: 'Closing Costs 2nd',
                    subLabel: '(Est. 2%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-commission',
                    label: 'less Commissions',
                    subLabel: '(Est. 5%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'dispo',
                    label: 'Disposition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'less-insurance',
                    label: 'Less Insurance while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-utils',
                    label: 'Less Utilities while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'taxes',
                    label: 'Annual Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'taxes-while-held',
                    label: 'Property Taxes while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'misc',
                    label: 'Miscellaneous',
                    subLabel: '(8% of Rehab)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'holding',
                    label: 'Holding Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'total',
                    label: 'Total Expenses',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [
                {
                    id: 'net-income',
                    label: 'Net Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'roi',
                    label: 'ROI',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    public aaFlip_financeBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'down-payment',
                    label: 'Down Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'amount-financed',
                    label: 'Amount Financed',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'points',
                    label: 'Points',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'loan-rate',
                    label: 'Loan Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        },
        {
            fields: [
                {
                    id: 'annual-loan-payment',
                    label: 'Annual Loan Payment Amount',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'points-private',
                    label: 'Loan Points',
                    subLabel: '(private money)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'monthly-loan-payment',
                    label: 'Monthly Loan Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'total-payment',
                    label: 'Total Loan Payments',
                    subLabel: '(Based Upon Hold Time)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        },
        {
            fields: [
                {
                    id: 'holding-plus-loan',
                    label: 'Holding Costs + Loan Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'total-cash',
                    label: 'Total Cash Out of Pocket',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [
                {
                    id: 'net-income-after-debt',
                    label: 'Net Income After Debt Service',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-on-cash',
                    label: 'Cash-on-Cash Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    // AA Rental Sections
    public aaRental_sharedTopBox: ICalculatorSection[] = [
        {
            tabIndexStart: 1,
            fields: [
                {
                    id: 'purchase-price',
                    label: 'Purchase Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'commission',
                    label: 'Commission for Selling Agent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        }
    ];

    public aaRental_cashBox: ICalculatorSection[] = [

        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'closing-cost',
                    label: 'Estimated Closing Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }

            ]
        },
        {
            fields: [
                {
                    id: 'acqui-cost',
                    label: 'Total Acquisition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'rent',
                    label: 'Monthly Rent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        },
        {
            fields: [
                {
                    id: 'gross-income',
                    label: 'Annual Gross Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'return',
                    label: 'Annual Gross Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'man-fee',
                    label: 'Less Management Fee',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'man-reserve',
                    label: 'Less Maintenance Reserve',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-vacancy',
                    label: 'Less 10% Vacancy',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-pest',
                    label: 'Less Pest Control / Landscaping',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-taxes',
                    label: 'Less Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-insurance',
                    label: 'Less Insurance',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-hoa',
                    label: 'Less HOA',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'total-expenses',
                    label: 'Total Annual Expenses',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        },
        {
            fields: [
                {
                    id: 'net-income',
                    label: 'Annual Net Operating Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-flow',
                    label: 'Monthly Net Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cap-rate',
                    label: 'Capitalization Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'plus-equity',
                    label: 'Plus Equity Position',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                }
            ]
        }
    ];

    public aaRental_financeBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'down-payment',
                    label: 'Down Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'amount-financed',
                    label: 'Amount Financed',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'points',
                    label: 'Points',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'loan-rate',
                    label: 'Loan Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'balloon-month',
                    label: 'Balloon Payoff',
                    subLabel: '(Months)',
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: null,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        },
        {
            fields: [
                {
                    id: 'points-paid',
                    label: 'Points Paid',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'annual-loan-payment',
                    label: 'Annual Payment',
                    subLabel: '(Debt Service)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'monthly-loan-payment',
                    label: 'Monthly Loan Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'total-payment',
                    label: 'Total Annual + Debt',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                }
            ]
        },
        {
            fields: [
                {
                    id: 'annual-net',
                    label: 'Annual Net Income',
                    subLabel: '(After Debt Service)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-flow',
                    label: 'Monthly Net Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [

                {
                    id: 'cash-out-of-pocket',
                    label: 'Total Cash Out of Pocket',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-on-cash',
                    label: 'Annual Cash-on-Cash Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    // Flip Analysis Sections
    public flipAnalysis_sharedTopBox: ICalculatorSection[] = [
        {
            tabIndexStart: 1,
            fields: [
                {
                    id: 'purchase-price',
                    label: 'Purchase Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'hold-time',
                    label: 'Hold Time',
                    subLabel: '(months)',
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: null,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        }
    ];

    public flipAnalysis_cashBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'simo-cc',
                    label: 'Estimated Simo CC',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'acqui-cost',
                    label: 'Total Acquisition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [
                {
                    id: 'second-closing',
                    label: 'Closing Costs 2nd',
                    subLabel: '(Est. 2%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-commission',
                    label: 'less Commissions',
                    subLabel: '(Est. 5%)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'dispo',
                    label: 'Disposition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'less-insurance',
                    label: 'Less Insurance while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'mo-hoa',
                    label: 'Mo HOA fees',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'hoa-held',
                    label: 'HOA while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'less-utils',
                    label: 'Less Utilities while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'taxes',
                    label: 'Annual Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'taxes-while-held',
                    label: 'Property Taxes while held',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'misc',
                    label: 'Miscellaneous',
                    subLabel: '(8% of Rehab)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },
                {
                    id: 'holding',
                    label: 'Holding Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'total',
                    label: 'Total Expenses',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
            ]
        },
        {
            fields: [
                {
                    id: 'net-income',
                    label: 'Net Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'roi',
                    label: 'ROI',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
            ]
        }
    ];

    public flipAnalysis_financeBox: ICalculatorSection[] = [

        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'down-payment',
                    label: 'Down Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'rehab-financed',
                    label: 'Addtl. Rehab Financed',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'amount-financed',
                    label: 'Amount Financed',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'points',
                    label: 'Points',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'loan-rate',
                    label: 'Loan Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        },
        {
            fields: [
                {
                    id: 'annual-loan-payment',
                    label: 'Annual Loan Payment Amount',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'points-private',
                    label: 'Loan Points',
                    subLabel: '(private money)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'monthly-loan-payment',
                    label: 'Monthly Loan Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'total-payment',
                    label: 'Total Loan Payments',
                    subLabel: '(Based Upon Hold Time)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                }
            ]
        },
        {
            fields: [
                {
                    id: 'holding-plus-loan',
                    label: 'Holding Costs + Loan Costs',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'total-cash',
                    label: 'Total Cash Out of Pocket',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [
                {
                    id: 'net-income-after-debt',
                    label: 'Net Income After Debt Service',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-on-cash',
                    label: 'Cash-on-Cash Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    // Rental Analysis Sections
    public rentalAnalysis_sharedTopBox: ICalculatorSection[] = [
        {
            tabIndexStart: 1,
            fields: [
                {
                    id: 'purchase-price',
                    label: 'Purchase Price',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'reno-cost',
                    label: 'Estimated Renovation Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'arv',
                    label: 'After Repair Value',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        }
    ];

    public rentalAnalysis_cashBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'simo-cc',
                    label: 'Estimated Simo CC',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'acqui-cost',
                    label: 'Total Acquisition Cost',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'hoa-monthly',
                    label: 'HOA monthly',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'rent-monthly',
                    label: 'Monthly Rent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'rent',
                    label: 'Annual Potential Rent',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null
                },

            ]
        },
        {
            fields: [
                {
                    id: 'gross-income',
                    label: 'Annual Gross Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'gross-return',
                    label: 'Annual Gross Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'man-fee',
                    label: 'less Management Fee',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'man-reserve',
                    label: 'Less Maintenance Reserve',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-vacancy',
                    label: 'Less 10% Vacancy',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'less-pest',
                    label: 'Less Pest Control / Landscaping',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-taxes',
                    label: 'Less Property Taxes',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-insurance',
                    label: 'Less Insurance',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'less-hoa',
                    label: 'Less HOA',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'total-expenses',
                    label: 'Total Annual Expenses',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
            ]
        },
        {
            fields: [
                {
                    id: 'net-income',
                    label: 'Annual Net Operating Income',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-flow',
                    label: 'Monthly Net Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cap-rate',
                    label: 'Capitalization Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'plus-equity',
                    label: 'Plus Equity Position',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
            ]
        }
    ];

    public rentalAnalysis_financeBox: ICalculatorSection[] = [
        {
            // tabIndexStart: 1,
            fields: [
                {
                    id: 'down-payment',
                    label: 'Down Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.IN_AND_OUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'amount-financed',
                    label: 'Amount Financed',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'points',
                    label: 'Points',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'loan-rate',
                    label: 'Loan Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'balloon-month',
                    label: 'Balloon Payoff',
                    subLabel: '(Months)',
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: null,
                    formControl: new FormControl('', [Validators.required])
                }
            ]
        },
        {
            fields: [
                {
                    id: 'points-paid',
                    label: 'Points Paid',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'monthly-loan-payment',
                    label: 'Monthly Loan Payment',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'annual-loan-payment',
                    label: 'Loan Payments',
                    subLabel: '(Debt Service)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'total-payment',
                    label: 'Total Loan Expenses + Debt Service',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                }
            ]
        },
        {
            fields: [
                {
                    id: 'annual-net',
                    label: 'Net Income',
                    subLabel: '(After Debt Service)',
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-flow',
                    label: 'Monthly Net Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        },
        {
            fields: [

                {
                    id: 'cash-out-of-pocket',
                    label: 'Total Cash Out of Pocket',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'cash-on-cash',
                    label: 'Annual Cash-on-Cash Return',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                }
            ]
        }
    ];

    public rentalAnalysis_refinanceBox: ICalculatorSection[] = [
        {
            fields: [
                {
                    id: 'bank-ltv',
                    label: 'Bank\'s LTV Ratio',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'refi-amount',
                    label: 'Refinance Loan Amount',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'refi-rate',
                    label: 'Refinance Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.INPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: new FormControl('', [Validators.required])
                },
                {
                    id: 'refi-cc',
                    label: 'Refinance CC plus Loan Fees',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'refi-monthly-payment',
                    label: 'Monthly Payment (20yr)',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
                {
                    id: 'refi-cash-out',
                    label: 'Cash Out',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                },
            ]
        },
        {
            fields: [
                {
                    id: 'net-income-after-refi',
                    label: 'Annual Net Income After Refinance',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'adjusted-net-cash',
                    label: 'Adjusted Monthly Net Cash Flow',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'leveraged-cap',
                    label: 'Leveraged Capitalization Rate',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.PERCENT,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
                {
                    id: 'remaining-net-equity',
                    label: 'Remaining Net Equity Position',
                    subLabel: null,
                    type: CALCULATOR_FIELD_TYPES.OUTPUT,
                    unit: CALCULATOR_FIELD_UNITS.DOLLAR,
                    formControl: null,
                    outputValue: null,
                    highlight: true
                },
            ]
        }
    ];

    public saving: boolean = false;
    public savingDone: boolean = false;
    public lastSaveDate: Date | undefined = undefined;
    // public autoSaveStatus = new BehaviorSubject<IAutoSaveStatus>({ inProgress: false, complete: false });
    public sharedValue: boolean | undefined = undefined;
    public isAddressFormValid: boolean = false;
    public streetViewImage: string | null | undefined;
    public errorMessage: IVestaMessage | null | undefined;

    public assigningVendor: boolean = false;

    get currentRoute() {
        const urlParts: string[] = this.router.url.split('?')[0].split('/');

        urlParts.pop();

        return urlParts.join('/');
    }

    constructor(
        private auth: AuthService,
        private orgService: OrganizationService,
        private propertyService: PropertyService,
        private titleCasePipe: TitleCasePipe,
        private upperCasePipe: UpperCasePipe,
        private router: Router
    ) {
        // this.autoSaveStatus.subscribe(this.autoSaveStatusChange);
    }

    resetState() {
        this.resetAddressForm();
        this.resetEstatedForm();
        this.resetMarketingForm();
        this.savingNew = false;
        this.fetchingEstatedData = false;
        this.isNew = false;
        this.addressUnlocked = false;
        this.streetViewImage = null;
        this.saving = false;
        this.savingDone = false;
        this.lastSaveDate = undefined;
        this.sharedValue = undefined;
        this.isAddressFormValid = false;
        this.errorMessage = undefined;

        this.property = new Parse.Object('Property');
        this.estatedInfo = new Parse.Object('EstatedInfo');
        this.offerFlip = new Parse.Object('Offer_Flip');
        this.offerRental = new Parse.Object('Offer_Rental');
        this.aaFlip = new Parse.Object('AA_Flip');
        this.aaRental = new Parse.Object('AA_Rental');
        this.saFlip = new Parse.Object('SA_Flip');
        this.saRental = new Parse.Object('SA_Rental');
        this.rehabFlip = new Parse.Object('Rehab_Flip');
        this.rehabRental = new Parse.Object('Rehab_Rental');
        this.vendors = [];
    }

    resetContract() {
        this.contract = new Parse.Object('Contract');
    }

    setPointersOnProperty() {
        this.property.set('estatedInfo', this.estatedInfo);
        this.property.set('offerFlip', this.offerFlip);
        this.property.set('offerRental', this.offerRental);
        this.property.set('aaFlip', this.aaFlip);
        this.property.set('aaRental', this.aaRental);
        this.property.set('saFlip', this.saFlip);
        this.property.set('saRental', this.saRental);
        this.property.set('rehabFlip', this.rehabFlip);
        this.property.set('rehabRental', this.rehabRental);
    }

    setOrganizationPointers() {
        this.property.set('organization', this.orgService.organization);
        this.estatedInfo.set('organization', this.orgService.organization);
        this.offerFlip.set('organization', this.orgService.organization);
        this.offerRental.set('organization', this.orgService.organization);
        this.aaFlip.set('organization', this.orgService.organization);
        this.aaRental.set('organization', this.orgService.organization);
        this.saFlip.set('organization', this.orgService.organization);
        this.saRental.set('organization', this.orgService.organization);
        this.rehabFlip.set('organization', this.orgService.organization);
        this.rehabRental.set('organization', this.orgService.organization);
    }

    setPropertyPointerOnChildObjects() {
        this.estatedInfo.set('property', this.property);
        this.offerFlip.set('property', this.property);
        this.offerRental.set('property', this.property);
        this.aaFlip.set('property', this.property);
        this.aaRental.set('property', this.property);
        this.saFlip.set('property', this.property);
        this.saRental.set('property', this.property);
        this.rehabFlip.set('property', this.property);
        this.rehabRental.set('property', this.property);
    }

    resetAddressForm() {
        this.addressForm = {
            address: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            type: '',
            status: '',
            offerTerms: ''
        };
    }

    hydrateAddressForm() {
        this.addressForm = {
            address: this.titleCasePipe.transform(this.property.get('address')),
            address2: this.titleCasePipe.transform(this.property.get('address2')),
            city: this.titleCasePipe.transform(this.property.get('city')),
            state: this.upperCasePipe.transform(this.property.get('state')),
            zip: this.property.get('zip'),
            type: this.property.get('type'),
            status: this.property.get('status'),
            offerTerms: this.property.get('offerTerms')
        };
    }

    resetEstatedForm() {
        this.estatedForm = {
            structure: {},
            parcel: {},
            deeds: [{}],
            assessments: [{}],
            taxes: [{}]
        };
    }

    hydrateEstatedForm() {
        // Creating deep copies of the objects to allow for proper discarding of changes.
        this.estatedForm.taxes = JSON.parse(JSON.stringify(this.estatedInfo.get('taxes') || [{}]));
        this.estatedForm.structure = JSON.parse(JSON.stringify(this.estatedInfo.get('structure') || {}));
        this.estatedForm.parcel = JSON.parse(JSON.stringify(this.estatedInfo.get('parcel') || {}));
        this.estatedForm.deeds = JSON.parse(JSON.stringify(this.estatedInfo.get('deeds') || [{}]));
        this.estatedForm.assessments = JSON.parse(JSON.stringify(this.estatedInfo.get('assessments') || [{}]));
    }

    resetMarketingForm() {
        this.marketingForm = {
            marketingTitle: '',
            marketingDescription: '',
            access: '',
            marketingPurchasePrice: 0,
            acceptedOfferPrice: 0,
            deposit: 0,
            inspectionExpiration: new Date(),
            closeDate: new Date(),
            noInspectionDate: false,
            hideZillowInfo: false
        };
    }

    hydrateMarketingForm() {
        this.marketingForm = {
            marketingTitle: this.property.get('marketingTitle') || '',
            marketingDescription: this.property.get('marketingDescription') || '',
            access: this.property.get('access') || '',
            marketingPurchasePrice: this.property.get('marketingPurchasePrice') || 0,
            acceptedOfferPrice: this.property.get('acceptedOfferPrice') || 0,
            deposit: this.property.get('deposit') || 0,
            inspectionExpiration: this.property.get('inspectionExpiration') || new Date(),
            closeDate: this.property.get('closeDate') || new Date(),
            noInspectionDate: this.property.get('noInspectionDate') || false,
            hideZillowInfo: this.property.get('hideZillowInfo') || false
        };
    }

    setStreetViewImage() {
        this.streetViewImage = this.propertyService.getStreetViewUrl(
            this.property.get('address'),
            this.property.get('city'),
            this.property.get('zip'),
            this.property.get('state'),
            640,
            460
        );
    }

    autoSaveStatusChange(payload: IAutoSaveStatus) {
        if (payload.complete) {
            this.saving = false;
            this.savingDone = true;
            this.lastSaveDate = new Date();
            // this.reload = true;

            if (!this.orgService.isPI) {
                this.sharedValue = this.property.get('shared');
            }
        } else if (payload.inProgress) {
            this.saving = true;
            this.savingDone = false;
        }
    }
}
